import { createClerkSupabaseClient } from '../../lib/supabase.js';

/**
 * Servicio avanzado para el dashboard de compañías con navegación jerárquica
 * Implementa métricas realistas y datos reales de Supabase
 */
class EnhancedDashboardService {
  constructor(session) {
    this.supabase = createClerkSupabaseClient(session);
  }

  /**
   * Obtiene métricas generales de la compañía usando la vista company_dashboard_metrics
   */
  async getCompanyMetrics(companyId, timeframe = '30d') {
    try {
      console.log('🔍 EnhancedDashboardService.getCompanyMetrics - Iniciando consulta');
      console.log('📋 Parámetros recibidos:', { companyId, timeframe });
      console.log('🔗 Cliente Supabase inicializado:', !!this.supabase);
      
      // Verificar que companyId no sea undefined o null
      if (!companyId) {
        console.error('❌ companyId es requerido pero no fue proporcionado');
        throw new Error('companyId es requerido');
      }

      console.log('🔍 Consultando vista company_dashboard_metrics...');
      
      const { data, error } = await this.supabase
        .from('company_dashboard_metrics')
        .select('*')
        .eq('company_id', companyId)
        .single();

      console.log('📊 Respuesta de Supabase:');
      console.log('  - Error:', error);
      console.log('  - Data:', data);
      console.log('  - Tipo de data:', typeof data);
      console.log('  - Es array:', Array.isArray(data));

      if (error) {
        console.error('❌ Error en consulta de métricas:', error);
        console.error('❌ Código de error:', error.code);
        console.error('❌ Mensaje:', error.message);
        console.error('❌ Detalles:', error.details);
        
        // Si la vista no existe o no hay datos, devolver métricas vacías
        if (error.code === 'PGRST116' || error.message?.includes('No rows found')) {
          console.warn('⚠️ No se encontraron datos para la compañía, devolviendo métricas vacías');
          return this.getEmptyMetrics();
        }
        
        throw error;
      }

      if (!data) {
        console.warn('⚠️ No se encontraron datos para la compañía:', companyId);
        return this.getEmptyMetrics();
      }

      console.log('✅ Datos encontrados, procesando métricas...');
      
      const processedMetrics = {
        totalSessions: data.total_sesiones || 0,
        totalQuotations: data.total_cotizaciones || 0,
        totalContractions: data.total_contrataciones || 0,
        totalCertificates: data.total_certificados || 0,
        totalSubscriptions: data.total_suscripciones || 0,
        conversionRates: {
          sessionToQuotation: data.tasa_sesion_cotizacion || 0,
          quotationToContract: data.tasa_cotizacion_contratacion || 0,
          contractToCertificate: data.tasa_contratacion_certificado || 0
        },
        revenue: {
          totalRevenueUF: data.monto_total_pagos || 0,
          avgContractValueUF: data.total_contrataciones > 0 ? (data.monto_total_pagos / data.total_contrataciones) : 0,
          monthlyRecurringRevenueUF: data.ingresos_mensuales_recurrentes || 0
        },
        trends: {
          sessions: { direction: 'stable', percentage: 0 },
          quotations: { direction: 'stable', percentage: 0 },
          contractions: { direction: 'stable', percentage: 0 },
          certificates: { direction: 'stable', percentage: 0 }
        }
      };

      console.log('✅ Métricas procesadas:', processedMetrics);
      return processedMetrics;

    } catch (error) {
      console.error('❌ Error crítico en getCompanyMetrics:', error);
      console.error('❌ Stack trace:', error.stack);
      return this.getEmptyMetrics();
    }
  }

  /**
   * Devuelve métricas vacías como fallback
   */
  getEmptyMetrics() {
    console.log('📋 Devolviendo métricas vacías como fallback');
    return {
      totalSessions: 0,
      totalQuotations: 0,
      totalContractions: 0,
      totalCertificates: 0,
      totalSubscriptions: 0,
      conversionRates: {
        sessionToQuotation: 0,
        quotationToContract: 0,
        contractToCertificate: 0
      },
      revenue: {
        totalRevenueUF: 0,
        avgContractValueUF: 0,
        monthlyRecurringRevenueUF: 0
      },
      trends: {
        sessions: { direction: 'stable', percentage: 0 },
        quotations: { direction: 'stable', percentage: 0 },
        contractions: { direction: 'stable', percentage: 0 },
        certificates: { direction: 'stable', percentage: 0 }
      }
    };
  }

  /**
   * Obtiene métricas de sesiones
   */
  async getSessionsMetrics(companyId, timeframe) {
    try {
      console.log('🔍 Consultando sesiones para compañía:', companyId);
      
      // Usar la vista company_sesiones que ya filtra por compañía
      const { data, error } = await this.supabase
        .from('company_sesiones')
        .select('*')
        .eq('compania_id', companyId)
        .gte('created_at', this.getDateFromTimeframe(timeframe));

      if (error) {
        console.error('❌ Error en consulta de sesiones:', error);
        throw error;
      }

      console.log('📊 Sesiones encontradas:', data?.length || 0);

      const total = data?.length || 0;
      const converted = data?.filter(s => s.cotizacion_generada)?.length || 0;
      
      return {
        total,
        converted,
        conversionRate: total > 0 ? (converted / total) * 100 : 0,
        trend: this.calculateTrend(data, 'created_at')
      };
    } catch (error) {
      console.error('❌ Error obteniendo métricas de sesiones:', error);
      return { total: 0, converted: 0, conversionRate: 0, trend: { direction: 'stable', percentage: 0 } };
    }
  }

  /**
   * Obtiene métricas de cotizaciones
   */
  async getQuotationsMetrics(companyId, timeframe) {
    try {
      console.log('🔍 Consultando cotizaciones para compañía:', companyId);
      
      // Usar la vista company_cotizaciones que ya filtra por compañía
      const { data, error } = await this.supabase
        .from('company_cotizaciones')
        .select('*')
        .eq('compania_id', companyId)
        .gte('created_at', this.getDateFromTimeframe(timeframe));

      if (error) {
        console.error('❌ Error en consulta de cotizaciones:', error);
        throw error;
      }

      console.log('📊 Cotizaciones encontradas:', data?.length || 0);

      const total = data?.length || 0;
      const converted = data?.filter(q => q.contratacion_generada)?.length || 0;
      const totalValue = data?.reduce((sum, q) => sum + (q.valor_uf || 0), 0) || 0;

      return {
        total,
        converted,
        totalValue,
        avgValue: total > 0 ? totalValue / total : 0,
        conversionRate: total > 0 ? (converted / total) * 100 : 0,
        trend: this.calculateTrend(data, 'created_at')
      };
    } catch (error) {
      console.error('❌ Error obteniendo métricas de cotizaciones:', error);
      return { total: 0, converted: 0, totalValue: 0, avgValue: 0, conversionRate: 0, trend: { direction: 'stable', percentage: 0 } };
    }
  }

  /**
   * Obtiene métricas de contrataciones
   */
  async getContractionsMetrics(companyId, timeframe) {
    try {
      console.log('🔍 Consultando contrataciones para compañía:', companyId);
      
      // Usar la vista company_contrataciones que ya filtra por compañía
      const { data, error } = await this.supabase
        .from('company_contrataciones')
        .select('*')
        .eq('compania_id', companyId)
        .gte('created_at', this.getDateFromTimeframe(timeframe));

      if (error) {
        console.error('❌ Error en consulta de contrataciones:', error);
        throw error;
      }

      console.log('📊 Contrataciones encontradas:', data?.length || 0);

      const total = data?.length || 0;
      const withCertificates = data?.filter(c => c.certificado_generado)?.length || 0;
      const totalValue = data?.reduce((sum, c) => sum + (c.valor_uf || 0), 0) || 0;

      return {
        total,
        withCertificates,
        totalValue,
        avgValue: total > 0 ? totalValue / total : 0,
        certificateRate: total > 0 ? (withCertificates / total) * 100 : 0,
        trend: this.calculateTrend(data, 'created_at')
      };
    } catch (error) {
      console.error('❌ Error obteniendo métricas de contrataciones:', error);
      return { total: 0, withCertificates: 0, totalValue: 0, avgValue: 0, certificateRate: 0, trend: { direction: 'stable', percentage: 0 } };
    }
  }

  /**
   * Obtiene métricas de certificados
   */
  async getCertificatesMetrics(companyId, timeframe) {
    try {
      console.log('🔍 Consultando certificados para compañía:', companyId);
      
      // Usar la vista company_certificados que ya filtra por compañía
      const { data, error } = await this.supabase
        .from('company_certificados')
        .select('*')
        .eq('compania_id', companyId)
        .gte('created_at', this.getDateFromTimeframe(timeframe));

      if (error) {
        console.error('❌ Error en consulta de certificados:', error);
        throw error;
      }

      console.log('📊 Certificados encontrados:', data?.length || 0);

      const total = data?.length || 0;
      const active = data?.filter(c => {
        const now = new Date();
        const endDate = new Date(c.fecha_termino_cobertura);
        return endDate > now;
      })?.length || 0;
      
      const expired = total - active;
      const expiringSoon = data?.filter(c => {
        const expiryDate = new Date(c.fecha_termino_cobertura);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
        const now = new Date();
        return expiryDate <= thirtyDaysFromNow && expiryDate > now;
      })?.length || 0;

      return {
        total,
        active,
        expired,
        expiringSoon,
        trend: this.calculateTrend(data, 'created_at')
      };
    } catch (error) {
      console.error('❌ Error obteniendo métricas de certificados:', error);
      return { total: 0, active: 0, expired: 0, expiringSoon: 0, trend: { direction: 'stable', percentage: 0 } };
    }
  }

  /**
   * Obtiene métricas de suscripciones
   */
  async getSubscriptionsMetrics(companyId, timeframe) {
    try {
      console.log('🔍 Consultando suscripciones para compañía:', companyId);
      
      const { data, error } = await this.supabase
        .from('subscriptions')
        .select('*')
        .eq('company_id', companyId);

      if (error) {
        console.error('❌ Error en consulta de suscripciones:', error);
        throw error;
      }

      console.log('📊 Suscripciones encontradas:', data?.length || 0);

      const active = data?.filter(s => s.status === 'activa')?.length || 0;
      const canceled = data?.filter(s => s.status === 'cancelada')?.length || 0;
      const mrr = data?.filter(s => s.status === 'activa')
        ?.reduce((sum, s) => sum + (s.amount || 0), 0) || 0;

      // Calcular churn rate (simplificado)
      const total = active + canceled;
      const churnRate = total > 0 ? (canceled / total) * 100 : 0;

      return {
        active,
        canceled,
        total,
        mrr,
        churnRate,
        trend: this.calculateTrend(data, 'created_at')
      };
    } catch (error) {
      console.error('❌ Error obteniendo métricas de suscripciones:', error);
      return { active: 0, canceled: 0, total: 0, mrr: 0, churnRate: 0, trend: { direction: 'stable', percentage: 0 } };
    }
  }

  /**
   * Obtiene rendimiento por producto usando las vistas de compañía
   */
  async getProductPerformance(companyId, timeframe = '30d') {
    try {
      console.log('🔍 EnhancedDashboardService.getProductPerformance - Iniciando consulta');
      console.log('📋 Parámetros:', { companyId, timeframe });

      if (!companyId) {
        console.error('❌ companyId es requerido para getProductPerformance');
        return [];
      }

      console.log('🔍 Consultando vista company_productos...');
      
      // Usar la tabla producto para obtener productos de la compañía
      const { data: productos, error: productError } = await this.supabase
        .from('producto')
        .select('*')
        .eq('compania_id', companyId);

      console.log('📊 Respuesta de company_productos:');
      console.log('  - Error:', productError);
      console.log('  - Data:', productos);
      console.log('  - Cantidad de productos:', productos?.length || 0);

      if (productError) {
        console.error('❌ Error en consulta de productos:', productError);
        return [];
      }

      if (!productos || productos.length === 0) {
        console.warn('⚠️ No se encontraron productos para la compañía:', companyId);
        return [];
      }

      console.log('✅ Productos encontrados, procesando rendimiento...');

      // Procesar cada producto para obtener métricas
      const productPerformance = await Promise.all(
        productos.map(async (producto) => {
          console.log(`📊 Procesando producto: ${producto.nombre} (ID: ${producto.id})`);
          
          try {
            // Obtener cotizaciones del producto usando la vista
            const { data: cotizaciones, error: quotError } = await this.supabase
              .from('company_cotizaciones')
              .select('*')
              .eq('compania_id', companyId)
              .eq('producto_id', producto.id);

            console.log(`  - Cotizaciones encontradas: ${cotizaciones?.length || 0}`);

            // Obtener contrataciones del producto usando la vista
            const { data: contrataciones, error: contractError } = await this.supabase
              .from('company_contrataciones')
              .select('*')
              .eq('compania_id', companyId)
              .eq('producto_id', producto.id);

            console.log(`  - Contrataciones encontradas: ${contrataciones?.length || 0}`);

            if (quotError) {
              console.error(`❌ Error obteniendo cotizaciones para producto ${producto.id}:`, quotError);
            }

            if (contractError) {
              console.error(`❌ Error obteniendo contrataciones para producto ${producto.id}:`, contractError);
            }

            const totalQuotations = cotizaciones?.length || 0;
            const totalContracts = contrataciones?.length || 0;
            const conversionRate = totalQuotations > 0 ? (totalContracts / totalQuotations) * 100 : 0;
            const revenue = contrataciones?.reduce((sum, c) => sum + (c.valor_uf || 0), 0) || 0;

            const productData = {
              productId: producto.id,
              productName: producto.nombre,
              quotations: totalQuotations,
              contracts: totalContracts,
              conversionRate: Math.round(conversionRate * 100) / 100,
              revenue: revenue
            };

            console.log(`✅ Datos procesados para ${producto.nombre}:`, productData);
            return productData;

          } catch (productError) {
            console.error(`❌ Error procesando producto ${producto.id}:`, productError);
            return {
              productId: producto.id,
              productName: producto.nombre,
              quotations: 0,
              contracts: 0,
              conversionRate: 0,
              revenue: 0
            };
          }
        })
      );

      console.log('✅ Rendimiento de productos procesado:', productPerformance);
      return productPerformance;

    } catch (error) {
      console.error('❌ Error crítico en getProductPerformance:', error);
      console.error('❌ Stack trace:', error.stack);
      return [];
    }
  }

  /**
   * Obtiene rendimiento por plan
   */
  async getPlanPerformance(companyId, productId = null) {
    try {
      console.log('🔍 Consultando rendimiento por plan para compañía:', companyId, 'producto:', productId);
      
      let query = this.supabase
        .from('company_planes')
        .select('*')
        .eq('compania_id', companyId);

      if (productId) {
        query = query.eq('producto_id', productId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Error en consulta de planes:', error);
        throw error;
      }

      console.log('📊 Planes encontrados:', data?.length || 0);

      return data?.map(plan => {
        // Para obtener métricas detalladas, necesitaríamos hacer consultas adicionales
        // Por ahora devolvemos datos básicos del plan
        return {
          planId: plan.id,
          planName: plan.nombre,
          productName: plan.producto_nombre || 'N/A',
          totalQuotations: 0, // Requiere consulta adicional
          totalContractions: 0, // Requiere consulta adicional
          totalCertificates: 0, // Requiere consulta adicional
          activeSubscriptions: 0, // Requiere consulta adicional
          totalRevenueUF: 0, // Requiere consulta adicional
          avgContractValueUF: 0,
          conversionRate: 0
        };
      }) || [];
    } catch (error) {
      console.error('❌ Error obteniendo rendimiento por plan:', error);
      return [];
    }
  }

  /**
   * Obtiene datos del embudo de conversión
   */
  async getConversionFunnel(companyId, timeframe = '30d') {
    try {
      console.log('🔍 EnhancedDashboardService.getConversionFunnel - Iniciando consulta');
      console.log('📋 Parámetros:', { companyId, timeframe });

      if (!companyId) {
        console.error('❌ companyId es requerido para getConversionFunnel');
        return this.getEmptyFunnelData();
      }

      console.log('🔍 Obteniendo métricas base para el embudo...');
      
      const metrics = await this.getCompanyMetrics(companyId, timeframe);
      
      console.log('📊 Métricas obtenidas para embudo:', metrics);

      if (!metrics) {
        console.warn('⚠️ No se pudieron obtener métricas para el embudo');
        return this.getEmptyFunnelData();
      }

      const funnelData = {
        sessions: {
          count: metrics.totalSessions,
          percentage: 100
        },
        quotations: {
          count: metrics.totalQuotations,
          percentage: metrics.totalSessions > 0 ? Math.round((metrics.totalQuotations / metrics.totalSessions) * 100) : 0
        },
        contractions: {
          count: metrics.totalContractions,
          percentage: metrics.totalSessions > 0 ? Math.round((metrics.totalContractions / metrics.totalSessions) * 100) : 0
        },
        subscriptions: {
          count: metrics.totalSubscriptions,
          percentage: metrics.totalSessions > 0 ? Math.round((metrics.totalSubscriptions / metrics.totalSessions) * 100) : 0
        },
        certificates: {
          count: metrics.totalCertificates,
          percentage: metrics.totalSessions > 0 ? Math.round((metrics.totalCertificates / metrics.totalSessions) * 100) : 0
        }
      };

      console.log('✅ Datos del embudo procesados:', funnelData);
      return funnelData;

    } catch (error) {
      console.error('❌ Error crítico en getConversionFunnel:', error);
      console.error('❌ Stack trace:', error.stack);
      return this.getEmptyFunnelData();
    }
  }

  /**
   * Obtiene las vistas company_* para construir el embudo por compañía
   */
  async getCompanyViewsSource(companyId, timeframe = '30d') {
    try {
      if (!companyId) return { cotizaciones: [], contrataciones: [], suscripciones: [], certificados: [] };
      const startDate = this.getDateFromTimeframe(timeframe);
      const endDate = new Date().toISOString();
      const [cotRes, conRes, sesRes, susRes, cerRes] = await Promise.all([
        this.supabase.from('company_cotizaciones').select('*,created_at').eq('compania_id', companyId).gte('created_at', startDate).lte('created_at', endDate),
        this.supabase.from('company_contrataciones').select('*,created_at').eq('compania_id', companyId).gte('created_at', startDate).lte('created_at', endDate),
        this.supabase.from('company_sesiones').select('id,compania_id,created_at').eq('compania_id', companyId),
        this.supabase.from('company_suscripciones').select('*,created_at').eq('company_id', companyId),
        this.supabase.from('company_certificados').select('*,created_at').eq('compania_id', companyId)
      ]);
      return {
        cotizaciones: Array.isArray(cotRes?.data) ? cotRes.data : [],
        contrataciones: Array.isArray(conRes?.data) ? conRes.data : [],
        suscripciones: Array.isArray(susRes?.data) ? susRes.data : [],
        certificados: Array.isArray(cerRes?.data) ? cerRes.data : [],
        sesiones: Array.isArray(sesRes?.data) ? sesRes.data : []
      };
    } catch (error) {
      console.error('❌ Error obteniendo vistas para embudo:', error);
      return { cotizaciones: [], contrataciones: [], suscripciones: [], certificados: [], sesiones: [] };
    }
  }

  /**
   * Devuelve datos vacíos del embudo como fallback
   */
  getEmptyFunnelData() {
    console.log('📋 Devolviendo datos vacíos del embudo como fallback');
    return {
      sessions: { count: 0, percentage: 100 },
      quotations: { count: 0, percentage: 0 },
      contracts: { count: 0, percentage: 0 },
      subscriptions: { count: 0, percentage: 0 },
      certificates: { count: 0, percentage: 0 }
    };
  }

  /**
   * Calcula tasas de conversión realistas
   */
  calculateConversionRates(sessions, quotations, contractions, certificates) {
    const sessionToQuotation = sessions.total > 0 ? 
      Math.min((quotations.total / sessions.total) * 100, 15) : 0; // Max 15%
    
    const quotationToContraction = quotations.total > 0 ? 
      Math.min((contractions.total / quotations.total) * 100, 25) : 0; // Max 25%
    
    const contractionToCertificate = contractions.total > 0 ? 
      Math.min((certificates.total / contractions.total) * 100, 95) : 0; // Max 95%

    return {
      sessionToQuotation: Math.round(sessionToQuotation * 100) / 100,
      quotationToContraction: Math.round(quotationToContraction * 100) / 100,
      contractionToCertificate: Math.round(contractionToCertificate * 100) / 100
    };
  }

  /**
   * Calcula tendencia basada en datos históricos
   */
  calculateTrend(data, dateField) {
    if (!data || data.length < 2) {
      return { direction: 'stable', percentage: 0 };
    }

    // Dividir datos en dos mitades para comparar
    const sortedData = data.sort((a, b) => new Date(a[dateField]) - new Date(b[dateField]));
    const midPoint = Math.floor(sortedData.length / 2);
    const firstHalf = sortedData.slice(0, midPoint);
    const secondHalf = sortedData.slice(midPoint);

    const firstHalfCount = firstHalf.length;
    const secondHalfCount = secondHalf.length;

    if (firstHalfCount === 0) return { direction: 'stable', percentage: 0 };

    const percentageChange = ((secondHalfCount - firstHalfCount) / firstHalfCount) * 100;
    
    let direction = 'stable';
    if (percentageChange > 5) direction = 'up';
    else if (percentageChange < -5) direction = 'down';

    return {
      direction,
      percentage: Math.round(Math.abs(percentageChange) * 100) / 100
    };
  }

  /**
   * Obtiene fecha basada en timeframe
   */
  getDateFromTimeframe(timeframe) {
    const now = new Date();
    const days = parseInt(timeframe.replace('d', ''));
    const date = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
    return date.toISOString();
  }

  /**
   * Obtiene tendencias de métricas usando la vista company_dashboard_metrics
   */
  async getMetricsTrends(companyId, timeframe = '30d') {
    try {
      console.log('🔍 Obteniendo tendencias de métricas para compañía:', companyId);
      
      const endDate = new Date();
      const startDate = this.getDateFromTimeframe(timeframe);
      
      // Generar fechas para el período
      const dates = this.generateDateRange(startDate, endDate);
      
      // Obtener datos por fecha usando las vistas de compañía
      const trendsData = await Promise.all(
        dates.map(async (date) => {
          const dayStart = new Date(date);
          const dayEnd = new Date(date);
          dayEnd.setHours(23, 59, 59, 999);
          
          try {
            // Sesiones del día - usar vista company_sesiones
            const { data: sessions } = await this.supabase
              .from('company_sesiones')
              .select('*')
              .eq('compania_id', companyId)
              .gte('created_at', dayStart.toISOString())
              .lte('created_at', dayEnd.toISOString());
            
            // Cotizaciones del día - usar vista company_cotizaciones
            const { data: quotations } = await this.supabase
              .from('company_cotizaciones')
              .select('*')
              .eq('compania_id', companyId)
              .gte('created_at', dayStart.toISOString())
              .lte('created_at', dayEnd.toISOString());
            
            // Contrataciones del día - usar vista company_contrataciones
            const { data: contractions } = await this.supabase
              .from('company_contrataciones')
              .select('*')
              .eq('compania_id', companyId)
              .gte('created_at', dayStart.toISOString())
              .lte('created_at', dayEnd.toISOString());
            
            // Certificados del día - usar vista company_certificados
            const { data: certificates } = await this.supabase
              .from('company_certificados')
              .select('*')
              .eq('compania_id', companyId)
              .gte('created_at', dayStart.toISOString())
              .lte('created_at', dayEnd.toISOString());
            
            return {
              date: date.toISOString().split('T')[0],
              sessions: sessions?.length || 0,
              quotations: quotations?.length || 0,
              contractions: contractions?.length || 0,
              certificates: certificates?.length || 0
            };
          } catch (dayError) {
            console.error(`❌ Error obteniendo datos para ${date.toISOString().split('T')[0]}:`, dayError);
            return {
              date: date.toISOString().split('T')[0],
              sessions: 0,
              quotations: 0,
              contractions: 0,
              certificates: 0
            };
          }
        })
      );
      
      return trendsData;
    } catch (error) {
      console.error('❌ Error obteniendo tendencias de métricas:', error);
      return [];
    }
  }

  /**
   * Genera un rango de fechas
   */
  generateDateRange(startDate, endDate) {
    const dates = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  }

  /**
   * Obtiene métricas vacías por defecto
   */
  getEmptyMetrics() {
    return {
      totalSessions: 0,
      totalQuotations: 0,
      totalContractions: 0,
      totalCertificates: 0,
      totalSubscriptions: 0,
      conversionRates: {
        sessionToQuotation: 0,
        quotationToContract: 0,
        contractToPayment: 0
      },
      revenueMetrics: {
        totalRevenueUF: 0,
        avgContractValueUF: 0,
        monthlyRecurringRevenueUF: 0
      },
      trends: {
        sessions: { direction: 'stable', percentage: 0 },
        quotations: { direction: 'stable', percentage: 0 },
        contractions: { direction: 'stable', percentage: 0 },
        certificates: { direction: 'stable', percentage: 0 }
      }
    };
  }
}

export default EnhancedDashboardService;
